import { computed, makeObservable, observable, runInAction } from 'mobx'

import type { ClientAppType } from '@campai/lib-common/entities'

export type WatchCounter = {
    counter: number
    reconcileRevision: number
    updatedAt: Date | null
    version: number
}

type WatchCounterSubscription = {
    unsubscribe: () => void
}

type WatchCounterValue = Pick<WatchCounter, 'counter' | 'reconcileRevision' | 'updatedAt'>

type WatchCounterValues = {
    notifications: WatchCounterValue
    teams: WatchCounterValue
}

type WatchCounterSubscriptionObserver = {
    onComplete: () => void
    onConnectionStateChange: (state: { error: unknown; state: string }) => void
    onData: (counters: WatchCounterValues) => void
    onError: (error: unknown) => void
    onStarted: () => void
    onStopped: () => void
}

type WatchCounterSubscriber = (
    input: {
        organizationId?: string
        targets: ClientAppType[]
    },
    observer: WatchCounterSubscriptionObserver
) => WatchCounterSubscription

type StartWatchCountersParams = {
    organizationId?: string
}

const emptyCounter = (): WatchCounter => ({
    counter: 0,
    reconcileRevision: 0,
    updatedAt: null,
    version: 0,
})

const updateCounter = (current: WatchCounter, next: WatchCounterValue): WatchCounter =>
    current.counter === next.counter &&
    current.reconcileRevision === next.reconcileRevision &&
    current.updatedAt?.getTime() === next.updatedAt?.getTime()
        ? current
        : { ...next, version: current.version + 1 }

export class WatchCounters {
    notifications = emptyCounter()
    teams = emptyCounter()

    readonly disabled: boolean
    private readonly subscribe: WatchCounterSubscriber
    private readonly target: ClientAppType
    private generation = 0
    private restartAttempt = 0
    private restartTimeout: ReturnType<typeof setTimeout> | null = null
    private startParams: StartWatchCountersParams | null = null
    private subscription: WatchCounterSubscription | null = null

    constructor({
        disabled = false,
        subscribe,
        target,
    }: {
        disabled?: boolean
        subscribe: WatchCounterSubscriber
        target: ClientAppType
    }) {
        this.disabled = disabled
        this.subscribe = subscribe
        this.target = target

        makeObservable(this, {
            notifications: observable,
            teams: observable,
            total: computed,
        })

        window.addEventListener('online', this.handleAvailabilityChange)
        window.addEventListener('offline', this.handleAvailabilityChange)
        document.addEventListener('visibilitychange', this.handleAvailabilityChange)
    }

    get total() {
        return this.notifications.counter + this.teams.counter
    }

    start(params: StartWatchCountersParams = {}) {
        this.stop()
        this.startParams = params
        this.handleAvailabilityChange()
    }

    private readonly handleAvailabilityChange = () => {
        if (!this.canSubscribe()) {
            this.clearRestartTimeout()
            this.stopSubscriptions()
            return
        }

        this.restartAttempt = 0
        this.clearRestartTimeout()
        if (!this.subscription) {
            this.startSubscriptions()
        }
    }

    private canSubscribe() {
        return (
            !!this.startParams &&
            !this.disabled &&
            navigator.onLine &&
            document.visibilityState === 'visible'
        )
    }

    private startSubscriptions() {
        const params = this.startParams
        if (!params || !this.canSubscribe()) {
            return
        }

        const generation = ++this.generation
        this.subscription = this.subscribe(
            {
                targets: [this.target],
                ...(params.organizationId ? { organizationId: params.organizationId } : {}),
            },
            {
                onStarted: () => undefined,
                onConnectionStateChange: ({ error, state }) => {
                    if (generation === this.generation && state === 'connecting' && error) {
                        this.scheduleRestart(generation)
                    }
                },
                onData: (counters) => {
                    if (generation !== this.generation) {
                        return
                    }

                    runInAction(() => {
                        this.restartAttempt = 0
                        this.notifications = updateCounter(this.notifications, counters.notifications)
                        this.teams = updateCounter(this.teams, counters.teams)
                    })
                },
                onError: (error) => {
                    console.error(`Failed to watch ${this.target} counters`, error)
                    this.scheduleRestart(generation)
                },
                onComplete: () => {
                    this.scheduleRestart(generation)
                },
                onStopped: () => {
                    this.scheduleRestart(generation)
                },
            }
        )
    }

    private scheduleRestart(generation: number) {
        if (generation !== this.generation || this.restartTimeout || !this.startParams) {
            return
        }

        this.stopSubscriptions()
        if (!this.canSubscribe()) {
            return
        }

        const maxDelay = Math.min(1_000 * 2 ** this.restartAttempt, 30_000)
        const delay = maxDelay / 2 + Math.random() * (maxDelay / 2)
        this.restartAttempt += 1
        this.restartTimeout = setTimeout(() => {
            this.restartTimeout = null
            this.startSubscriptions()
        }, delay)
    }

    private stop() {
        this.startParams = null
        this.restartAttempt = 0
        this.clearRestartTimeout()
        this.stopSubscriptions()
    }

    private clearRestartTimeout() {
        if (this.restartTimeout) {
            clearTimeout(this.restartTimeout)
            this.restartTimeout = null
        }
    }

    private stopSubscriptions() {
        this.generation += 1
        const activeSubscription = this.subscription
        this.subscription = null
        activeSubscription?.unsubscribe()
    }
}
